import React, { useMemo } from 'react'
import ZionForm, { useZionForm } from '@fs/zion-form'
import { FlowGrid, Paragraph } from '@fs/zion-ui'
import { Trans } from 'react-i18next'
import { useLinkablePersons } from '../hooks/useLinkablePersons'
import { getGroupedPersonsSelectionDefaults, FORM_ID } from '../utils/formDefaults'
import GroupedPersonCheckboxes from './GroupedPersonCheckboxes'

/**
 * LinkNamesPanelContent component for managing which persons are linked to a specific field.
 * It displays grouped checkboxes for persons in the same record, same image, and other images,
 * allowing users to link or unlink them from the field.
 *
 * On form submission, it computes which persons need to be linked or unlinked
 * and calls the onSave callback with those changes.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.person - The current person object
 * @param {Object} props.field - The field object containing displayLabel, label, and value
 * @param {Function} props.onSave - Callback function invoked when changes are saved
 * @param {Array|null} props.linkedPersonIds - Array of person IDs already linked to the element
 * @param {Object} props.overlay - Overlay from the useOverlay hook for controlling the panel.
 * @param {string} [props.description] - Optional custom description text
 * @param {Function} props.viewInContextHandler - Handler for viewing person in context (Opening split screen view)
 * @returns {React.ReactElement} The rendered component
 */
const LinkNamesPanelContent = ({
  person,
  field,
  onSave,
  linkedPersonIds = null,
  overlay,
  description,
  viewInContextHandler,
}) => {
  const { personsInThisImage, personsInThisRecord, personsInOtherImages, personsLinkedToElementOnCurrentImage } =
    useLinkablePersons({
      person,
      field,
      linkedPersonIds,
    })

  const formDefaultValues = useMemo(
    () =>
      getGroupedPersonsSelectionDefaults({
        personIdsSharingElement: personsLinkedToElementOnCurrentImage.map((p) => p.id),
        allTheArksExceptCurrent: personsInOtherImages,
        personsInThisRecord,
        personsInThisImage,
      }),
    [personsLinkedToElementOnCurrentImage, personsInThisRecord, personsInThisImage, personsInOtherImages]
  )

  const methods = useZionForm({
    mode: 'all',
    defaultValues: formDefaultValues,
  })

  const computeLinkChanges = (initialValues, currentValues) => {
    const toLinkSet = new Set()
    const toUnlinkSet = new Set()

    // Combine all sections to check for changes
    const allSections = [
      currentValues.linkStatesInRecord,
      currentValues.linkStatesInImage,
      currentValues.linkStatesInOtherArks,
    ]

    const allInitialSections = [
      initialValues.linkStatesInRecord,
      initialValues.linkStatesInImage,
      initialValues.linkStatesInOtherArks,
    ]

    allSections.forEach((section, sectionIndex) => {
      const initialSection = allInitialSections[sectionIndex]

      Object.entries(section).forEach(([personId, isLinked]) => {
        const wasLinked = initialSection[personId]

        if (isLinked && !wasLinked) {
          toLinkSet.add(personId)
          toUnlinkSet.delete(personId) // Remove from unlink if it appears in multiple sections
        } else if (!isLinked && wasLinked) {
          toUnlinkSet.add(personId)
          toLinkSet.delete(personId) // Remove from link if it appears in multiple sections
        }
      })
    })

    // Count total persons currently linked in this and other images to determine if the field is shared
    const totalLinked = [
      ...Object.values(currentValues.linkStatesInImage),
      ...Object.values(currentValues.linkStatesInOtherArks),
    ].filter(Boolean).length
    const isSharedField = totalLinked > 1

    return {
      toLink: Array.from(toLinkSet),
      toUnlink: Array.from(toUnlinkSet),
      isSharedField,
    }
  }

  const handleSubmit = (formValues) => {
    const changes = computeLinkChanges(formDefaultValues, formValues)
    onSave?.({ ...changes, person, field })
    overlay.close()
  }

  const displayLabel = field?.displayLabel || field?.label
  const displayValue = field?.value

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="sm">
      <Paragraph size="sm">
        {description || (
          <Trans
            i18nKey="linkNamesPanel.instructions"
            values={{ displayLabel, displayValue }}
            components={{ bold: <b /> }}
          >
            Do more names need to be linked to the {{ displayLabel }} <bold>{{ displayValue }}</bold>?
          </Trans>
        )}
      </Paragraph>
      <ZionForm methods={methods} onSubmit={handleSubmit} id={FORM_ID}>
        <GroupedPersonCheckboxes
          personsInThisRecord={personsInThisRecord}
          personsInThisImage={personsInThisImage}
          allTheArksExceptCurrent={personsInOtherImages}
          viewInContextHandler={viewInContextHandler}
          selectedPersonId={person.id}
        />
      </ZionForm>
    </FlowGrid>
  )
}

export default LinkNamesPanelContent
